import { PROTOCOL_VERSION } from '../lib/constants';
import { toBase64Url } from '../lib/crypto';
import type { PeerSession } from '../webrtc/peer';
import { applyGameMove, createGameState, signalCommitment } from './engine';
import type {
  GameKind,
  GameLoungeSnapshot,
  GameMove,
  GameProposal,
  PlayerSide,
  SignalChoice,
  SignalShowdownState,
} from './types';

const GAME_PROTOCOL_VERSION = 1;
const SUPPORTED_GAMES: GameKind[] = ['ferry-four', 'dock-boxes', 'signal-showdown', 'tic-tac-toe'];

type GameMessage =
  | { type: 'game-hello'; protocolVersion: number; gameProtocolVersion: number; games: GameKind[] }
  | { type: 'game-proposal'; protocolVersion: number; proposal: GameProposal }
  | { type: 'game-vote'; protocolVersion: number; proposalId: string; vote: 'accept' | 'decline' }
  | { type: 'game-withdraw'; protocolVersion: number; proposalId: string }
  | { type: 'game-move'; protocolVersion: number; sessionId: string; move: GameMove }
  | { type: 'game-leave'; protocolVersion: number; sessionId: string };

interface GameProtocolEvents {
  onChange: (snapshot: GameLoungeSnapshot) => void;
  onNotice: (message: string) => void;
}

interface PrivateSignal {
  round: number;
  choice: SignalChoice;
  nonce: string;
}

export class GameProtocol {
  private readonly peer: PeerSession;
  private readonly role: PlayerSide;
  private readonly events: GameProtocolEvents;
  private readonly proposals = new Map<string, GameProposal>();
  private readonly unsubscribe: () => void;
  private helloTimer?: number;
  private availabilityTimer?: number;
  private peerAvailable = false;
  private peerUnavailable = false;
  private session?: GameLoungeSnapshot['session'];
  private privateSignal?: PrivateSignal;
  private operation = Promise.resolve();

  constructor(peer: PeerSession, role: PlayerSide, events: GameProtocolEvents) {
    this.peer = peer;
    this.role = role;
    this.events = events;
    this.unsubscribe = peer.onControl((value) => {
      void this.enqueue(() => this.handle(value)).catch((error) => {
        this.events.onNotice(error instanceof Error ? error.message : 'The other browser sent an invalid game message.');
      });
    });
    this.sendHello();
    this.helloTimer = window.setInterval(() => this.sendHello(), 1_000);
    this.availabilityTimer = window.setTimeout(() => {
      if (!this.peerAvailable) {
        this.peerUnavailable = true;
        this.emit();
      }
    }, 6_000);
    this.emit();
  }

  propose(kind: GameKind): void {
    if (!this.peerAvailable) throw new Error('The other browser does not support this Passenger Lounge version yet.');
    if (this.session) throw new Error('Finish the current game before proposing another one.');
    if (!SUPPORTED_GAMES.includes(kind)) throw new Error('That game is not supported.');
    if ([...this.proposals.values()].some((proposal) => proposal.proposer === this.role && proposal.kind === kind)) {
      throw new Error('You already proposed that game.');
    }
    if (this.proposals.size >= 8) throw new Error('There are already too many game proposals.');
    const proposal: GameProposal = { id: crypto.randomUUID(), kind, proposer: this.role, createdAt: Date.now() };
    this.proposals.set(proposal.id, proposal);
    this.send({ type: 'game-proposal', protocolVersion: PROTOCOL_VERSION, proposal });
    this.emit();
  }

  accept(proposalId: string): void {
    const proposal = this.proposals.get(proposalId);
    if (!proposal || proposal.proposer === this.role) throw new Error('That game proposal is no longer available.');
    this.send({ type: 'game-vote', protocolVersion: PROTOCOL_VERSION, proposalId, vote: 'accept' });
    this.startSession(proposal);
  }

  decline(proposalId: string): void {
    const proposal = this.proposals.get(proposalId);
    if (!proposal) return;
    const ownProposal = proposal.proposer === this.role;
    this.send(ownProposal
      ? { type: 'game-withdraw', protocolVersion: PROTOCOL_VERSION, proposalId }
      : { type: 'game-vote', protocolVersion: PROTOCOL_VERSION, proposalId, vote: 'decline' });
    this.proposals.delete(proposalId);
    this.emit();
  }

  play(move: GameMove): Promise<void> {
    return this.enqueue(() => this.applyAndSend(move));
  }

  chooseSignal(choice: SignalChoice): Promise<void> {
    return this.enqueue(async () => {
      const state = this.signalState();
      if (state.commitments[this.role]) throw new Error('You already locked in a signal for this round.');
      const nonce = toBase64Url(crypto.getRandomValues(new Uint8Array(18)));
      this.privateSignal = { round: state.round, choice, nonce };
      const commitment = await signalCommitment(state.sessionId, state.round, choice, nonce);
      await this.applyAndSend({ type: 'signal-commit', round: state.round, commitment });
    });
  }

  continueSignal(): Promise<void> {
    const state = this.signalState();
    return this.play({ type: 'signal-next', round: state.round });
  }

  leave(): void {
    if (!this.session) return;
    this.send({ type: 'game-leave', protocolVersion: PROTOCOL_VERSION, sessionId: this.session.sessionId });
    this.session = undefined;
    this.privateSignal = undefined;
    this.proposals.clear();
    this.emit();
  }

  rematch(): void {
    if (!this.session) return;
    const kind = this.session.kind;
    this.leave();
    this.propose(kind);
  }

  snapshot(): GameLoungeSnapshot {
    return {
      peerAvailable: this.peerAvailable,
      peerUnavailable: this.peerUnavailable,
      proposals: this.session ? [] : [...this.proposals.values()].sort((left, right) => left.createdAt - right.createdAt),
      session: this.session ? structuredClone(this.session) : undefined,
      localSignalChoice: this.privateSignal?.choice,
    };
  }

  close(): void {
    this.unsubscribe();
    if (this.helloTimer) window.clearInterval(this.helloTimer);
    this.helloTimer = undefined;
    if (this.availabilityTimer) window.clearTimeout(this.availabilityTimer);
    this.availabilityTimer = undefined;
  }

  private async handle(value: unknown): Promise<void> {
    if (!isPotentialGameMessage(value)) return;
    if (!isGameMessage(value)) throw new Error('A malformed game message was ignored.');
    if (value.type === 'game-hello') {
      if (value.gameProtocolVersion !== GAME_PROTOCOL_VERSION || !SUPPORTED_GAMES.every((kind) => value.games.includes(kind))) return;
      const wasAvailable = this.peerAvailable;
      this.peerAvailable = true;
      this.peerUnavailable = false;
      if (this.helloTimer) window.clearInterval(this.helloTimer);
      this.helloTimer = undefined;
      if (this.availabilityTimer) window.clearTimeout(this.availabilityTimer);
      this.availabilityTimer = undefined;
      if (!wasAvailable) this.sendHello();
      this.emit();
      return;
    }
    if (!this.peerAvailable) return;
    if (value.type === 'game-proposal') {
      if (this.session || value.proposal.proposer !== otherSide(this.role) || this.proposals.size >= 8) return;
      this.proposals.set(value.proposal.id, value.proposal);
      this.emit();
      return;
    }
    if (value.type === 'game-vote') {
      const proposal = this.proposals.get(value.proposalId);
      if (!proposal || proposal.proposer !== this.role) return;
      if (value.vote === 'accept') this.startSession(proposal);
      else {
        this.proposals.delete(value.proposalId);
        this.events.onNotice('The other person declined the game proposal.');
        this.emit();
      }
      return;
    }
    if (value.type === 'game-withdraw') {
      const proposal = this.proposals.get(value.proposalId);
      if (proposal?.proposer === otherSide(this.role)) {
        this.proposals.delete(value.proposalId);
        this.emit();
      }
      return;
    }
    if (value.type === 'game-leave') {
      if (this.session?.sessionId === value.sessionId) {
        this.session = undefined;
        this.privateSignal = undefined;
        this.proposals.clear();
        this.events.onNotice('The other person left the game.');
        this.emit();
      }
      return;
    }
    if (!this.session || value.sessionId !== this.session.sessionId) return;
    this.session = await applyGameMove(this.session, otherSide(this.role), value.move);
    this.emit();
    await this.maybeRevealSignal();
  }

  private async applyAndSend(move: GameMove): Promise<void> {
    if (!this.session) throw new Error('There is no active game.');
    const sessionId = this.session.sessionId;
    this.session = await applyGameMove(this.session, this.role, move);
    this.send({ type: 'game-move', protocolVersion: PROTOCOL_VERSION, sessionId, move });
    this.emit();
    await this.maybeRevealSignal();
  }

  private async maybeRevealSignal(): Promise<void> {
    if (this.session?.kind !== 'signal-showdown' || !this.privateSignal) return;
    const signal = this.privateSignal;
    if (
      signal.round !== this.session.round
      || !this.session.commitments.host
      || !this.session.commitments.guest
      || this.session.reveals[this.role]
    ) return;
    await this.applyAndSend({ type: 'signal-reveal', round: signal.round, choice: signal.choice, nonce: signal.nonce });
  }

  private signalState(): SignalShowdownState {
    if (this.session?.kind !== 'signal-showdown') throw new Error('Signal Showdown is not active.');
    return this.session;
  }

  private startSession(proposal: GameProposal): void {
    if (this.session && this.session.sessionId.localeCompare(proposal.id) <= 0) return;
    this.session = createGameState(proposal.id, proposal.kind, proposal.proposer);
    this.privateSignal = undefined;
    this.emit();
  }

  private sendHello(): void {
    try {
      this.send({
        type: 'game-hello',
        protocolVersion: PROTOCOL_VERSION,
        gameProtocolVersion: GAME_PROTOCOL_VERSION,
        games: SUPPORTED_GAMES,
      });
    } catch {
      // The retry timer covers the short gap while a newly attached control channel settles.
    }
  }

  private send(message: GameMessage): void {
    this.peer.sendControl(message);
  }

  private enqueue(task: () => Promise<void>): Promise<void> {
    const run = this.operation.then(task, task);
    this.operation = run.catch(() => undefined);
    return run;
  }

  private emit(): void {
    this.events.onChange(this.snapshot());
  }
}

function otherSide(role: PlayerSide): PlayerSide {
  return role === 'host' ? 'guest' : 'host';
}

function isPotentialGameMessage(value: unknown): boolean {
  return Boolean(value && typeof value === 'object' && typeof (value as { type?: unknown }).type === 'string'
    && (value as { type: string }).type.startsWith('game-'));
}

function isGameMessage(value: unknown): value is GameMessage {
  if (!value || typeof value !== 'object' || JSON.stringify(value).length > 4_096) return false;
  const message = value as Partial<GameMessage>;
  if (message.protocolVersion !== PROTOCOL_VERSION || typeof message.type !== 'string') return false;
  if (message.type === 'game-hello') {
    return message.gameProtocolVersion === GAME_PROTOCOL_VERSION
      && Array.isArray(message.games)
      && message.games.length <= SUPPORTED_GAMES.length
      && message.games.every(isGameKind);
  }
  if (message.type === 'game-proposal') return isProposal(message.proposal);
  if (message.type === 'game-vote') return isId(message.proposalId) && (message.vote === 'accept' || message.vote === 'decline');
  if (message.type === 'game-withdraw') return isId(message.proposalId);
  if (message.type === 'game-leave') return isId(message.sessionId);
  return message.type === 'game-move' && isId(message.sessionId) && isMove(message.move);
}

function isProposal(value: unknown): value is GameProposal {
  if (!value || typeof value !== 'object') return false;
  const proposal = value as Partial<GameProposal>;
  return isId(proposal.id)
    && isGameKind(proposal.kind)
    && (proposal.proposer === 'host' || proposal.proposer === 'guest')
    && typeof proposal.createdAt === 'number'
    && Number.isSafeInteger(proposal.createdAt)
    && proposal.createdAt > 0;
}

function isMove(value: unknown): value is GameMove {
  if (!value || typeof value !== 'object') return false;
  const move = value as Partial<GameMove> & Record<string, unknown>;
  if (move.type === 'tic-cell') return isSmallInteger(move.index, 0, 8);
  if (move.type === 'ferry-column') return isSmallInteger(move.column, 0, 6);
  if (move.type === 'dock-edge') return (move.orientation === 'horizontal' || move.orientation === 'vertical') && isSmallInteger(move.index, 0, 19);
  if (move.type === 'signal-commit') return isSmallInteger(move.round, 1, 99) && typeof move.commitment === 'string' && /^[A-Za-z0-9_-]{43}$/u.test(move.commitment);
  if (move.type === 'signal-reveal') {
    return isSmallInteger(move.round, 1, 99)
      && (move.choice === 'rock' || move.choice === 'paper' || move.choice === 'scissors')
      && typeof move.nonce === 'string'
      && /^[A-Za-z0-9_-]{16,64}$/u.test(move.nonce);
  }
  return move.type === 'signal-next' && isSmallInteger(move.round, 1, 99);
}

function isSmallInteger(value: unknown, minimum: number, maximum: number): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= minimum && value <= maximum;
}

function isId(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{1,64}$/u.test(value);
}

function isGameKind(value: unknown): value is GameKind {
  return typeof value === 'string' && SUPPORTED_GAMES.includes(value as GameKind);
}
